import { motion } from "framer-motion";
import { ArrowLeftIcon, ArchiveIcon, TrashIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate, useParams } from "react-router";
import {
  ACCOUNTING_MONEY_CONTEXT,
  cn,
  formatMoney,
  isAccountingNotification,
} from "@reactive-resume/utils";

import {
  useArchiveNotification,
  useDeleteNotification,
  useNotification,
  useSyncNotificationRead,
} from "@/client/services/notification/queries";
import { useAccountCurrency } from "@/client/services/currency/currency";
import { useNotificationBody } from "@/client/services/notification/use-notification-body";
import { CATEGORY_LABELS, getEventDisplay } from "@/client/services/notification/event-display";

dayjs.extend(relativeTime);
dayjs.locale("fr");

/** Libellés français des clés de payload les plus courantes. */
const DATA_LABELS: Record<string, string> = {
  amount: "Montant",
  currency: "Devise",
  name: "Nom",
  email: "E-mail",
  userName: "Utilisateur",
  rateName: "Pack",
  score: "Score",
  validTo: "Valable jusqu'au",
  daysLeft: "Jours restants",
  quantity: "Quantité",
  total: "Total",
  views: "Vues",
  resumeId: "Référence CV",
  activated: "Compte réactivé",
  title: "CV",
};

const formatDataValue = (
  key: string,
  value: unknown,
  formatFcfa: (amount: number) => string,
): string => {
  if (value === null || value === undefined || value === "") return "—";
  switch (key) {
    case "amount": {
      return formatFcfa(Number(value));
    }
    case "validTo":
      return dayjs(String(value)).format("D MMMM YYYY");
    case "activated":
      return value ? "Oui" : "Non";
    default:
      return String(value);
  }
};

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PAGE DE DÉTAIL — lecture d'une notification complète
 * ─────────────────────────────────────────────────────────────────────────────
 *  Route : /client/user-space/notifications/:id
 *  L'ouverture marque la notification comme lue (côté serveur).
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const NotificationDetailPage = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data: notification, isLoading, isError } = useNotification(id);
  const archive = useArchiveNotification();
  const remove = useDeleteNotification();
  const notificationBody = useNotificationBody();

  // Le serveur marque la notification comme lue au GET /:id —
  // ce hook synchronise la liste, le compteur et les badges client.
  useSyncNotificationRead(id, notification?.isRead);

  const back = () => navigate("/client/user-space/notifications");

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 pb-24 pt-10 sm:px-6">
        <div className="h-4 w-24 animate-pulse rounded-md bg-canvas-subtle" />
        <div className="mt-6 space-y-3 rounded-2xl border border-border-subtle bg-surface p-6">
          <div className="h-3 w-24 animate-pulse rounded-md bg-canvas-subtle" />
          <div className="h-6 w-3/4 animate-pulse rounded-md bg-canvas-subtle" />
          <div className="h-4 w-full animate-pulse rounded-md bg-canvas-subtle/70" />
          <div className="h-4 w-4/5 animate-pulse rounded-md bg-canvas-subtle/70" />
        </div>
      </div>
    );
  }

  if (isError || !notification) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-3 px-4 pb-24 pt-16 text-center sm:px-6">
        <p className="text-sm font-semibold text-text">Notification introuvable</p>
        <p className="max-w-xs text-xs leading-relaxed text-text-muted">
          Elle a peut-être été supprimée ou n'appartient pas à votre compte.
        </p>
        <button
          type="button"
          onClick={back}
          className="mt-2 rounded-lg px-3 py-1.5 text-xs font-semibold text-[hsl(var(--product-brand))] transition-colors hover:bg-canvas-subtle"
        >
          Retour aux notifications
        </button>
      </div>
    );
  }

  const data = (notification.data ?? {}) as Record<string, unknown>;
  const dataEntries = Object.entries(data).filter(
    ([, value]) => value !== undefined && value !== null && value !== "",
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="mx-auto w-full max-w-2xl px-4 pb-24 pt-10 sm:px-6"
    >
      <button
        type="button"
        onClick={back}
        className="mb-6 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold text-text-muted transition-colors hover:bg-canvas-subtle hover:text-text"
      >
        <ArrowLeftIcon size={13} weight="bold" />
        Toutes les notifications
      </button>

      <DetailCard
        notificationId={notification.id}
        category={notification.category}
        type={notification.type}
        createdAt={notification.createdAt}
        title={notification.title}
        body={notificationBody(notification) ?? null}
        link={notification.link}
        data={data}
        dataEntries={dataEntries}
        onNavigate={navigate}
        onArchive={() => archive.mutate({ id: notification.id }, { onSuccess: back })}
        onDelete={() => remove.mutate(notification.id, { onSuccess: back })}
      />
    </motion.div>
  );
};

type DetailCardProps = {
  notificationId: string;
  category: string;
  type: string;
  createdAt: string | Date;
  title?: string | null;
  body?: string | null;
  link?: string | null;
  data: Record<string, unknown>;
  dataEntries: [string, unknown][];
  onNavigate: (path: string) => void;
  onArchive: () => void;
  onDelete: () => void;
};

const DetailCard = ({
  category,
  type,
  createdAt,
  title,
  body,
  link,
  data,
  dataEntries,
  onNavigate,
  onArchive,
  onDelete,
}: DetailCardProps) => {
  const { format } = useAccountCurrency();
  const formatAmount = isAccountingNotification(type)
    ? (amount: number) => formatMoney(amount, ACCOUNTING_MONEY_CONTEXT)
    : format;

  const display = getEventDisplay(type);
  const Icon = display.icon;

  return (
    <article className="overflow-hidden rounded-2xl border border-border-subtle bg-surface shadow-[0_1px_3px_rgba(20,14,44,0.04)]">
      {/* En-tête : tuile catégorie + dates */}
      <header className="border-b border-border-subtle/70 px-6 pb-5 pt-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className={cn(
                "grid size-11 shrink-0 place-items-center rounded-xl",
                display.className,
              )}
            >
              <Icon size={22} weight="duotone" />
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-text-muted">
              {CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS] ?? category}
            </span>
          </div>
          <time
            className="text-[11px] tabular-nums text-text-muted"
            title={dayjs(createdAt).format("dddd D MMMM YYYY à HH:mm")}
          >
            {dayjs(createdAt).fromNow()}
          </time>
        </div>

        <h1 className="mt-3 text-xl font-bold leading-snug tracking-tight text-text">
          {title ?? "Notification"}
        </h1>
        <p className="mt-1.5 text-xs text-text-muted">
          {dayjs(createdAt).format("dddd D MMMM YYYY à HH:mm")}
        </p>
      </header>

      {/* Corps */}
      {body && (
        <div className="px-6 py-6">
          <p className="whitespace-pre-line text-sm leading-relaxed text-text">{body}</p>
        </div>
      )}

      {/* Payload contextuel */}
      {dataEntries.length > 0 && (
        <dl className="border-t border-border-subtle/70 bg-canvas-subtle/30 px-6 py-4">
          {dataEntries.map(([key, value]) => (
            <div key={key} className="flex items-baseline justify-between gap-4 py-1.5">
              <dt className="text-xs font-medium text-text-muted">{DATA_LABELS[key] ?? key}</dt>
              <dd className="text-right text-xs font-semibold tabular-nums text-text">
                {formatDataValue(key, value, formatAmount)}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Actions */}
      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle/70 px-6 py-4">
        {link ? (
          <button
            type="button"
            onClick={() => onNavigate(link)}
            className="rounded-xl bg-[hsl(var(--product-brand))] px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          >
            Ouvrir la page concernée
          </button>
        ) : type.startsWith("HELP_") ? (
          <button
            type="button"
            onClick={() => onNavigate("/client/user-space/help")}
            className="rounded-xl bg-[hsl(var(--product-brand))] px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          >
            Ouvrir le centre d'aide
          </button>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Archiver"
            onClick={onArchive}
            className="grid size-8 place-items-center rounded-lg text-text-muted transition-colors hover:bg-canvas-subtle hover:text-text"
          >
            <ArchiveIcon size={15} weight="regular" />
          </button>
          <button
            type="button"
            aria-label="Supprimer"
            onClick={onDelete}
            className="grid size-8 place-items-center rounded-lg text-text-muted transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <TrashIcon size={15} weight="regular" />
          </button>
        </div>
      </footer>
    </article>
  );
};
